/**
 * REST interface endpoints — FE model + pure helpers (ADR-0051, the single
 * exposure registry). Mirrors `backend/src/hub/apps/rest_exposure.py`: a
 * `kind="rest"` interface's `config` holds a curated list of endpoints, each
 * over a TABLE (CRUD per-verb), a DATASET (`list` read), or a named QUERY.
 *
 * `endpointRoutes` is the FE twin of the backend `endpoint_route_specs` seam,
 * so the "what does this endpoint serve" preview can't drift from the router.
 */
import { uid } from "@/lib/uid";

export type RestSource = "table" | "dataset" | "query";
export type RestVerb = "list" | "get" | "create" | "update" | "delete" | "upsert";
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export type ParamLocation = "path" | "query" | "body";
export type ParamType = "string" | "number" | "boolean";

/** A declared HTTP param of a QUERY endpoint — the endpoint's HTTP surface, an ADAPTER
 *  over the Query (ADR-0057), not a 1:1 projection: the PUBLIC `name` is decoupled from
 *  the Query input it feeds (`maps_to`) and carries its own docs + default. */
export interface RestParam {
  /** The PUBLIC HTTP name (path `{name}` / query key / body key). */
  name: string;
  location: ParamLocation;
  type: ParamType;
  required: boolean;
  /** The Query input this param feeds (ADR-0057 L1). Empty = same as `name`. */
  maps_to?: string | null;
  /** Endpoint-level docs (wins over the Query input's in the OpenAPI). */
  description?: string | null;
  /** Value injected when the request omits the param. */
  default?: string | null;
}

export interface RestEndpoint {
  id: string;
  source: RestSource;
  target_id: string;
  /** A grouping label ("resource"): groups endpoints AND its slug is the endpoint's
   *  PATH ROOT (`path = /<slug(resource)><subpath>`). */
  resource?: string | null;
  /** A human description shown in the OpenAPI docs. */
  description?: string | null;
  /** table/dataset: which verbs are exposed; ignored for a query. */
  verbs: RestVerb[];
  path: string;
  /** query endpoints: the HTTP method (default POST). */
  method?: HttpMethod | null;
  /** query endpoints: the declared path/query/body param contract. */
  params?: RestParam[];
  /** query endpoints, OPTIONAL (ADR-0057 L2): a `{query_input: "{{ template }}"}` request
   *  map over the raw HTTP bag (`$`) — arbitrary transforms/renames/constants. When set it
   *  REPLACES the `maps_to` binding. */
  input_map?: Record<string, string> | null;
  run_as_role_id?: string | null;
  enabled: boolean;
}

export interface RestConfig {
  base_path?: string | null;
  endpoints: RestEndpoint[];
}

/** All CRUD verbs a TABLE endpoint may expose. */
export const TABLE_VERBS: RestVerb[] = ["list", "get", "create", "update", "delete", "upsert"];
/** A projection is read-only → the READ subset: `list` (collection) + `get` (by id). */
export const DATASET_VERBS: RestVerb[] = ["list", "get"];
export const PARAM_LOCATIONS: ParamLocation[] = ["path", "query", "body"];
export const PARAM_TYPES: ParamType[] = ["string", "number", "boolean"];

/** Per-verb HTTP mapping: [method, isItemPath] — mirrors the backend `_VERB_ROUTE`. */
const VERB_ROUTE: Record<RestVerb, [HttpMethod, boolean]> = {
  list: ["GET", false],
  create: ["POST", false],
  upsert: ["PUT", false],
  get: ["GET", true],
  update: ["PATCH", true],
  delete: ["DELETE", true],
};

export interface RouteSpec {
  method: HttpMethod;
  path: string;
  verb: RestVerb | null;
}

/** The (METHOD, path, verb) triples an endpoint serves (verb null for a query). */
export function endpointRoutes(ep: RestEndpoint): RouteSpec[] {
  if (ep.source === "query") {
    return [{ method: (ep.method || "POST") as HttpMethod, path: ep.path, verb: null }];
  }
  return ep.verbs.map((v) => {
    const [method, item] = VERB_ROUTE[v];
    return { method, path: item ? `${ep.path}/{id}` : ep.path, verb: v };
  });
}

/** The verbs allowed for a source (a query carries none — its verb is its method). */
export function allowedVerbs(source: RestSource): RestVerb[] {
  if (source === "query") return [];
  return source === "dataset" ? DATASET_VERBS : TABLE_VERBS;
}

/** A slug path segment from a friendly name (`"Deals 2024" → "/deals-2024"`). */
export function slugPath(name: string): string {
  const s = (name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return "/" + (s || "endpoint");
}

/** The path ROOT a resource defines (`"Aziende" → "/aziende"`; empty → ""). */
export function resourceRoot(resource: string | null | undefined): string {
  return (resource || "").trim() ? slugPath(resource as string) : "";
}

/** Compose the stored `path` from a resource root + an endpoint subpath. */
export function composePath(resource: string | null | undefined, subpath: string): string {
  const root = resourceRoot(resource);
  const sub = (subpath || "").trim();
  const suffix = sub && !sub.startsWith("/") ? `/${sub}` : sub;
  return (root + suffix) || "/endpoint";
}

/** The subpath under a resource root (the inverse of {@link composePath}). */
export function resourceSubpath(path: string, resource: string | null | undefined): string {
  const root = resourceRoot(resource);
  if (root && (path === root || path.startsWith(root + "/"))) return path.slice(root.length);
  return root ? "" : path; // path doesn't sit under the root → no subpath (root owns it)
}

/** A fresh endpoint for the given source, pre-filled with sensible defaults. */
export function emptyEndpoint(source: RestSource, targetId = "", path = "/endpoint"): RestEndpoint {
  return {
    id: uid("ep"),
    source,
    target_id: targetId,
    resource: null,
    verbs: source === "query" ? [] : [...allowedVerbs(source)],
    path,
    method: source === "query" ? "POST" : null,
    params: [],
    run_as_role_id: null,
    enabled: true,
  };
}

/** A fresh declared param. */
export function emptyParam(location: ParamLocation = "query"): RestParam {
  return { name: "", location, type: "string", required: location === "path" };
}

/** A single declared input of the referenced Query (ADR-0054 `input_schema` shape). */
export interface QueryInputRef {
  name: string;
  type: string; // string | number | boolean | object | array
  required: boolean;
  location?: string;
}

/** Bind a query endpoint's params to the referenced Query's declared input contract
 *  (ADR-0054): ONE param per Query input — name + required come from the Query, the type
 *  is coerced to the scalar RestParam set (object/array → string for the wire), and the
 *  LOCATION is the operator's choice (`locations` override by name) else the input's
 *  declared location (default `query`). This is the BINDING the endpoint editor renders
 *  so the endpoint params can't drift from the Query contract. */
export function bindQueryInputs(
  queryInputs: QueryInputRef[],
  locations: Record<string, ParamLocation> = {},
): RestParam[] {
  return queryInputs.map((qi) => {
    const declared = (qi.location as ParamLocation) || "query";
    const location = locations[qi.name] ?? (PARAM_LOCATIONS.includes(declared) ? declared : "query");
    const type: ParamType = qi.type === "number" || qi.type === "boolean" ? qi.type : "string";
    // BOUND mode (ADR-0057): `maps_to` is FIXED to the Query input this param feeds; the
    // PUBLIC `name` defaults to it but the operator may rename it (e.g. URL `{deal}` → `deal_id`).
    return { name: qi.name, maps_to: qi.name, type, required: qi.required, location };
  });
}

/** Coerce a Query input's declared type to the scalar RestParam type. */
function paramTypeOf(t: string): ParamType {
  return t === "number" || t === "boolean" ? t : "string";
}

/** The default location for a Query input (its declared one, or `query`). */
function defaultLocOf(qi: QueryInputRef): ParamLocation {
  const declared = (qi.location as ParamLocation) || "query";
  return PARAM_LOCATIONS.includes(declared) ? declared : "query";
}

/**
 * Reconcile a query endpoint's stored `params` against the referenced Query's input
 * contract, PRESERVING the operator's inclusion choices (ADR-0054 + unbind support).
 *
 * Unlike {@link bindQueryInputs} (which binds EVERY input), an operator may EXCLUDE an
 * OPTIONAL input from the endpoint (it simply isn't accepted/mapped — the Query still
 * tolerates it as optional). So this:
 *  - FRESH endpoint (no params yet) → include ALL inputs (the default to then prune);
 *  - otherwise KEEP the params already present (syncing type/required from the contract),
 *    AUTO-ADD any REQUIRED input missing (a required input MUST be bound or the call 422s),
 *    and NEVER re-add an OPTIONAL input the operator removed (its absence IS the exclusion).
 * Params whose name left the contract are dropped. Order follows the contract.
 */
export function reconcileBoundParams(bound: QueryInputRef[], params: RestParam[]): RestParam[] {
  if (params.length === 0) return bindQueryInputs(bound);
  // Key existing params by the Query input they FEED (`maps_to`), so a PUBLIC rename
  // (`name`) never loses the binding; fall back to `name` for legacy/unmapped params.
  const byInput = new Map(params.map((p) => [p.maps_to ?? p.name, p]));
  const out: RestParam[] = [];
  for (const qi of bound) {
    const existing = byInput.get(qi.name);
    if (existing) {
      // Keep the operator's public `name` + location + description; re-pin maps_to/type/required.
      out.push({ ...existing, maps_to: qi.name, type: paramTypeOf(qi.type), required: qi.required });
    } else if (qi.required) {
      out.push({ name: qi.name, maps_to: qi.name, type: paramTypeOf(qi.type), required: true, location: defaultLocOf(qi) });
    }
    // else: optional input absent from params → the operator EXCLUDED it → skip.
  }
  return out;
}

/** The `{name}` placeholders declared in a path template (mirrors the backend). */
export function pathPlaceholders(path: string): string[] {
  return Array.from((path || "").matchAll(/\{([a-z][a-z0-9_]*)\}/g), (m) => m[1]);
}

/**
 * Group endpoints by their `resource` label for the master list. Endpoints with no
 * resource fall under a "Generale" bucket. Groups + rows keep declaration order.
 */
export function groupByResource(
  endpoints: RestEndpoint[],
): { resource: string; endpoints: RestEndpoint[] }[] {
  const order: string[] = [];
  const byRes = new Map<string, RestEndpoint[]>();
  for (const ep of endpoints) {
    const key = (ep.resource || "").trim() || "Generale";
    if (!byRes.has(key)) {
      byRes.set(key, []);
      order.push(key);
    }
    byRes.get(key)!.push(ep);
  }
  return order.map((resource) => ({ resource, endpoints: byRes.get(resource)! }));
}

/**
 * Client-side param-contract check for a QUERY endpoint (mirrors the backend
 * `_validate_params`): names unique + every path `{placeholder}` has exactly one
 * `location:"path"` param and vice-versa. Returns an error code or null.
 */
export function firstParamError(ep: RestEndpoint): string | null {
  if (ep.source !== "query") return null;
  const params = ep.params ?? [];
  const seen = new Set<string>();
  const declaredPath = new Set<string>();
  for (const p of params) {
    if (!p.name.trim()) return "param_empty_name";
    if (seen.has(p.name)) return `duplicate_param:${p.name}`;
    seen.add(p.name);
    if (p.location === "path") declaredPath.add(p.name);
  }
  const placeholders = new Set(pathPlaceholders(ep.path));
  if (declaredPath.size !== placeholders.size || [...declaredPath].some((n) => !placeholders.has(n)))
    return "path_params_mismatch";
  return null;
}

/** Read the endpoints out of an interface `config` (tolerant of a missing/loose shape). */
export function endpointsOf(config: Record<string, unknown> | null | undefined): RestEndpoint[] {
  const raw = (config?.endpoints ?? []) as unknown;
  return Array.isArray(raw) ? (raw as RestEndpoint[]) : [];
}

/**
 * Client-side route-collision check (mirrors the backend `validate_rest_config`
 * so a bad config is caught before the save 422s). Returns the first conflicting
 * `"METHOD path"` string, or null when clean. Disabled endpoints don't collide.
 */
export function firstRouteConflict(endpoints: RestEndpoint[]): string | null {
  const seen = new Set<string>();
  for (const ep of endpoints) {
    if (!ep.enabled) continue;
    for (const r of endpointRoutes(ep)) {
      const key = `${r.method} ${r.path}`;
      if (seen.has(key)) return key;
      seen.add(key);
    }
  }
  return null;
}
